/**
 * Tile-level font overrides — the shared "Font family" / "Font size" Format
 * controls — applied onto a FULLY-BUILT ECharts option.
 *
 * Two passes:
 *  1. Walk the whole option and fill fontFamily/fontSize into every existing
 *     text-style bag (series labels, markLine labels, tooltip textStyle, axis
 *     labels, graphic text, …) WITHOUT overriding a value a builder set
 *     explicitly — per-chart controls like tooltipFontSize / labelFontSize
 *     stay authoritative over the tile-wide base font.
 *  2. Seed the bags ECharts does NOT inherit from the root `textStyle`
 *     (axis labels, axis names, legend, tooltip) so the override reaches
 *     every piece of chart text even when the builder never created the bag.
 *
 * `axisFontWeight` (the shared "Axis label weight" Format control) is scoped
 * NARROWER on purpose: it lands only on the X / Y axes' tick labels
 * (`axisLabel`) and axis titles (`nameTextStyle`) — never on series labels,
 * legend, or tooltip text — so axes can be highlighted independently of the
 * rest of the chart text. An explicit fontWeight a builder set wins. It is
 * either ONE value for every axis, or an `AxisFontWeights` map for per-axis
 * control (x / y = primary, x2 / y2 = the secondary axes at index 1+).
 *
 * All inputs are optional and loosely typed on purpose: they arrive straight
 * from a tile's `format` record ('' / 0 / undefined all mean "keep default").
 */

/** Keys whose object values are ECharts text-style bags. */
const TEXT_STYLE_KEYS = new Set([
  'textStyle',
  'subtextStyle',
  'nameTextStyle',
  'axisLabel',
  'label',
  'endLabel',
  'upperLabel',
  'edgeLabel',
]);

/** Per-axis weights for `applyChartFont` — x/y are the primary axes, x2/y2 the
 *  secondary X / secondary (dual) Y axes (ECharts axis index 1+). Values are
 *  ECharts/CSS fontWeight tokens; '' / undefined = keep that axis's default. */
export interface AxisFontWeights {
  x?: unknown;
  y?: unknown;
  x2?: unknown;
  y2?: unknown;
}

export function applyChartFont<T extends object>(
  option: T, fontFamily?: unknown, fontSize?: unknown, axisFontWeight?: unknown | AxisFontWeights,
): T {
  const family = String(fontFamily ?? '').trim();
  const sizeNumber = Number(fontSize);
  const size = Number.isFinite(sizeNumber) && sizeNumber > 0 ? sizeNumber : 0;
  // ECharts accepts 'normal'/'bold'/'bolder'/'lighter' or a number — numeric
  // strings from the select ("600") are converted so canvas text honors them.
  const normWeight = (v: unknown): string | number => {
    const s = String(v ?? '').trim();
    return /^\d+$/.test(s) ? Number(s) : s;
  };
  // A plain value fans out to every axis; an AxisFontWeights map is per-axis.
  const weightMap: AxisFontWeights = axisFontWeight && typeof axisFontWeight === 'object'
    ? axisFontWeight as AxisFontWeights
    : { x: axisFontWeight, y: axisFontWeight, x2: axisFontWeight, y2: axisFontWeight };
  const hasWeight = [weightMap.x, weightMap.y, weightMap.x2, weightMap.y2]
    .some((v) => String(v ?? '').trim() !== '');
  if (!family && !size && !hasWeight) return option;

  const fill = (bag: Record<string, unknown>): void => {
    if (family && bag['fontFamily'] === undefined) bag['fontFamily'] = family;
    if (size && bag['fontSize'] === undefined) bag['fontSize'] = size;
  };
  /** Create `parent[key]` when missing, then fill it (existing props win). */
  const ensure = (parent: Record<string, unknown>, key: string): void => {
    const existing = parent[key];
    if (existing === undefined || existing === null) parent[key] = {};
    const bag = parent[key];
    if (bag && typeof bag === 'object' && !Array.isArray(bag)) fill(bag as Record<string, unknown>);
  };

  // Pass 1 — fill every existing text-style bag anywhere in the option.
  const visit = (node: unknown, key?: string): void => {
    if (Array.isArray(node)) {
      node.forEach((child) => visit(child, key));
      return;
    }
    if (!node || typeof node !== 'object') return;
    const record = node as Record<string, unknown>;
    if (key && TEXT_STYLE_KEYS.has(key)) fill(record);
    Object.entries(record).forEach(([childKey, child]) => visit(child, childKey));
  };
  visit(option);

  // Pass 2 — seed the non-inheriting bags.
  const root = option as Record<string, unknown>;
  ensure(root, 'textStyle');
  const asList = (value: unknown): Array<Record<string, unknown>> =>
    (Array.isArray(value) ? value : value ? [value] : []).filter(
      (entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object',
    );
  // Axis-only weight: tick labels + axis title. Explicit builder values win.
  const weighAxis = (axis: Record<string, unknown>, w: unknown): void => {
    ensure(axis, 'axisLabel');
    ensure(axis, 'nameTextStyle');
    const weight = normWeight(w);
    if (weight === '') return;
    ['axisLabel', 'nameTextStyle'].forEach((key) => {
      const bag = axis[key];
      if (bag && typeof bag === 'object' && !Array.isArray(bag) && (bag as Record<string, unknown>)['fontWeight'] === undefined) {
        (bag as Record<string, unknown>)['fontWeight'] = weight;
      }
    });
  };
  asList(root['xAxis']).forEach((axis, i) => weighAxis(axis, i === 0 ? weightMap.x : weightMap.x2));
  asList(root['yAxis']).forEach((axis, i) => weighAxis(axis, i === 0 ? weightMap.y : weightMap.y2));
  asList(root['legend']).forEach((legend) => ensure(legend, 'textStyle'));
  asList(root['tooltip']).forEach((tooltip) => ensure(tooltip, 'textStyle'));
  return option;
}
